import { useState, useRef, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";
import { trackEvent } from "@/lib/analytics";
import PageMeta from "@/components/base/PageMeta";

const siteUrl = import.meta.env.VITE_PUBLIC_SITE_URL || "https://hackerslodge.com";

const categoryDescriptions: Record<string, string> = {
  "Social Media Hack": "Describe what you need done on which social media platform. Include account usernames, links, and specific goals.",
  "Blackhat Hacking": "Provide details about the target, the type of exploit you need, and the expected outcome. Be as specific as possible.",
  "Penetration Testing": "Specify the scope, target systems, testing methodology preferences, and whether you need a formal report.",
  "Database": "Describe the database type, target, what data you need accessed or modified, and any known details about the system.",
  "Spy": "Detail the surveillance target, what information you need gathered, duration, and any special requirements.",
  "Mobile Hacking": "Specify the device type (iOS/Android), target, what access level you need, and any known security measures.",
  "Email Hacking": "Provide the email address(es), what you need access to, and any relevant context about the target.",
  "Website Hacking": "Describe the website, CMS if known, what you need done, and any technical details you have.",
  "Cryptocurrency": "Detail the wallet address, transaction info, what recovery or tracing you need, and blockchain details.",
  "Other": "Describe your unique requirements in detail. Include all relevant information about your target and goals.",
};

export default function PostProjectCategoryPage() {
  return <PostProjectForm />;
}

function PostProjectForm() {
  const { category } = useParams<{ category: string }>();
  const decodedCategory = category ? decodeURIComponent(category) : "";
  const { user, profile, loading: authLoading, emailConfirmed, signIn, signUp, resetPassword, resendConfirmation } = useAuth();
  const navigate = useNavigate();
  const [formStatus, setFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(decodedCategory);
  const savedPayment = sessionStorage.getItem("selectedPaymentMethod") || "crypto";
  const [preferredPayment, setPreferredPayment] = useState(savedPayment);
  const formRef = useRef<HTMLFormElement>(null);
  const authSectionRef = useRef<HTMLDivElement>(null);

  /* ---- Processing / Success Popup ---- */
  const [popupStatus, setPopupStatus] = useState<"hidden" | "processing" | "success">("hidden");
  const processingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [submittedTitle, setSubmittedTitle] = useState("");

  useEffect(() => {
    return () => {
      if (processingTimerRef.current) clearTimeout(processingTimerRef.current);
    };
  }, []);

  /* ---- Inline Auth State ---- */
  const [authMode, setAuthMode] = useState<"login" | "signup" | "forgot">("login");
  const [authStatus, setAuthStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [authError, setAuthError] = useState("");
  const [resendStatus, setResendStatus] = useState<"idle" | "loading" | "sent">("idle");
  const authFormRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (decodedCategory) {
      setSelectedCategory(decodedCategory);
    }
  }, [decodedCategory]);

  useEffect(() => {
    // Reset auth state when user changes
    if (user) {
      setAuthStatus("idle");
      setAuthError("");
      setResendStatus("idle");
    }
  }, [user]);

  const handleResendConfirmation = async () => {
    if (!user?.email) return;
    setResendStatus("loading");
    const { error } = await resendConfirmation(user.email);
    if (error) {
      setResendStatus("idle");
    } else {
      setResendStatus("sent");
    }
  };

  const handleProjectSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!user) {
      // Scroll down to the auth section so user can login/signup
      authSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      setFormError("");
      if (formRef.current) formRef.current.reset();
      setTimeout(() => setFormStatus("idle"), 3000);
      return;
    }

    const title = (formData.get("project_title") as string) || "";
    const budget = (formData.get("budget") as string) || "";
    const deliveryTime = (formData.get("delivery_time") as string) || "";
    const description = (formData.get("description") as string) || "";
    const paymentMethod = preferredPayment || "crypto";

    setSubmittedTitle(title);
    setPopupStatus("processing");
    setFormError("");

    const fullDescription = `[Delivery: ${deliveryTime || "Not specified"}]\n\n${description}`;

    const supabase = getSupabase();
    const { data: insertedProject, error } = await supabase.from("projects").insert({
      client_id: user!.id,
      title,
      description: fullDescription,
      budget,
      category: selectedCategory,
      status: "open",
      review_status: "pending",
      payment_method: paymentMethod,
      delivery_time: deliveryTime || null,
    }).select("id").single();

    if (error) {
      setPopupStatus("hidden");
      setFormStatus("error");
      setFormError(error.message);
    } else {
      if (formRef.current) formRef.current.reset();

      // Fire-and-forget: send confirmation email
      if (insertedProject) {
        try {
          const { data: profileData } = await supabase
            .from("profiles")
            .select("email")
            .eq("id", user!.id)
            .maybeSingle();

          if (profileData?.email) {
            supabase.functions.invoke("project-submit-notify", {
              body: {
                email: profileData.email,
                project_title: title,
                project_category: selectedCategory,
                project_id: insertedProject.id,
                user_id: user!.id,
              },
            }).catch(() => {});
          }
        } catch {
          // notification failed silently — project still submitted
        }
      }

      // Fire GA4 conversion: post_project
      trackEvent("post_project", {
        project_category: selectedCategory,
        project_title: title,
        budget,
        payment_method: paymentMethod,
      });

      // Show processing for at least 2.5s then transition to success
      processingTimerRef.current = setTimeout(() => {
        setPopupStatus("success");
      }, 2500);
    }
  };

  const handleAuthSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("company_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setAuthStatus("success");
      setAuthError("");
      if (authFormRef.current) authFormRef.current.reset();
      setTimeout(() => setAuthStatus("idle"), 1500);
      return;
    }

    const email = (formData.get("email") as string) || "";
    setAuthStatus("loading");
    setAuthError("");

    if (authMode === "forgot") {
      const { error } = await resetPassword(email);
      if (error) {
        setAuthStatus("error");
        setAuthError(error);
      } else {
        setAuthStatus("success");
        setAuthError("");
        if (authFormRef.current) authFormRef.current.reset();
      }
      return;
    }

    const password = (formData.get("password") as string) || "";

    if (authMode === "login") {
      const { error } = await signIn(email, password);
      if (error) {
        setAuthStatus("error");
        setAuthError(error);
      }
    } else {
      const username = (formData.get("username") as string) || "";
      const confirmPassword = (formData.get("confirm_password") as string) || "";

      if (password !== confirmPassword) {
        setAuthStatus("error");
        setAuthError("Passwords do not match.");
        return;
      }

      if (password.length < 6) {
        setAuthStatus("error");
        setAuthError("Password must be at least 6 characters.");
        return;
      }

      const { error } = await signUp(email, password, username);
      if (error) {
        setAuthStatus("error");
        setAuthError(error);
      }
    }
  };

  const isValidCategory = selectedCategory && categoryDescriptions[selectedCategory];

  const categoryPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: `${siteUrl}/post-project/${encodeURIComponent(selectedCategory || "")}`,
    name: isValidCategory ? `Post a ${selectedCategory} Project — Hackers Lodge` : "Post a Hacking Project — Hackers Lodge",
    description: isValidCategory ? `Submit your ${selectedCategory.toLowerCase()} project and get matched with expert freelance hackers. ${categoryDescriptions[selectedCategory]}` : "Describe your hacking project and get matched with the best freelance hackers for the job.",
    isPartOf: {
      "@type": "WebSite",
      name: "Hackers Lodge",
      url: siteUrl,
    },
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#161b22] flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-3xl text-[#5bc0de] mb-3 block" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#161b22]">
      <PageMeta
        title={isValidCategory ? `Post a ${selectedCategory} Project — Hire ${selectedCategory} Specialists` : "Post a Hacking Project — Hackers Lodge"}
        description={isValidCategory ? `${categoryDescriptions[selectedCategory]} Submit your ${selectedCategory.toLowerCase()} project on Hackers Lodge and get matched with expert freelance hackers.` : "Describe your hacking project and get matched with the best freelance hackers. All submissions are private and confidential."}
        keywords={isValidCategory ? `${selectedCategory.toLowerCase()}, hire hacker, post project, cybersecurity, ethical hacking` : "hire hacker, post hacking project, cybersecurity, ethical hacker, security project"}
        canonicalUrl={`${siteUrl}/post-project/${encodeURIComponent(selectedCategory || "")}`}
        ogType="website"
        schema={categoryPageSchema}
      />
      <Header />
      <main className="pt-20 md:pt-24">
        <section className="py-10 md:py-14">
          <div className="w-full px-4 md:px-8 max-w-3xl mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-xs text-gray-400 mb-6">
              <Link to="/post-project" className="hover:text-[#5cb85c] transition-colors">
                Service Selection
              </Link>
              <i className="fas fa-chevron-right text-[10px]" />
              <span className="text-gray-400 font-medium">{selectedCategory || "Post Project"}</span>
            </div>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-[#5cb85c]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-paper-plane text-[#5cb85c] text-xl" />
              </div>
              <h1 className="text-2xl md:text-3xl font-light text-gray-200 mb-2">
                {isValidCategory ? `Post a ${selectedCategory} Project` : "Post a Project"}
              </h1>
              <p className="text-sm text-gray-500 max-w-md mx-auto">
                {isValidCategory
                  ? categoryDescriptions[selectedCategory]
                  : "Describe your project and get matched with the best hackers for the job. All submissions are private and confidential."}
              </p>
            </div>

            {/* Email Verification Banner — shown when logged in but email not confirmed */}
            {user && !emailConfirmed && (
              <div className="mb-6 p-4 bg-amber-900/20 border border-amber-700/30 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="fas fa-envelope text-amber-400 text-sm" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-amber-400 mb-1">Email not confirmed</p>
                    <p className="text-xs text-amber-400 leading-relaxed mb-3">
                      We sent a confirmation link to <strong>{user.email}</strong>. Check your inbox and click the link to verify your account before posting projects.
                    </p>
                    <button
                      type="button"
                      onClick={handleResendConfirmation}
                      disabled={resendStatus !== "idle"}
                      className="text-xs font-semibold text-amber-400 hover:text-amber-900 underline transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      {resendStatus === "loading" ? (
                        <span><i className="fas fa-spinner fa-spin mr-1" />Sending...</span>
                      ) : resendStatus === "sent" ? (
                        <span><i className="fas fa-check mr-1" />Resent! Check your inbox</span>
                      ) : (
                        <span>Resend confirmation email</span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Project Form — always visible and interactive, placed ABOVE auth */}
            <form ref={formRef} onSubmit={handleProjectSubmit} className="bg-[#0d1117] rounded border border-gray-700 p-6 md:p-8">
              {/* Honeypot */}
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />

              {/* Logged-in user banner */}
              {user && (
                <div className="mb-6 p-3 bg-green-900/20 border border-green-700/30 rounded flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-[#5cb85c] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {(profile?.username || profile?.display_name || user.email || "U").charAt(0).toUpperCase()}
                  </div>
                  <span className="text-green-400">
                    Posting as <strong>{profile?.username || profile?.display_name || user.email}</strong>
                  </span>
                </div>
              )}

              {/* Project Title */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-300 mb-1.5">
                  Project Title <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="project_title"
                  required
                  placeholder={`e.g., ${selectedCategory === "Social Media Hack" ? "Instagram Account Recovery" : selectedCategory === "Penetration Testing" ? "Web Application Security Audit" : "Describe your project in a few words"}`}
                  className="w-full px-4 py-2.5 border border-gray-600 rounded bg-[#0d1117] text-sm focus:outline-none focus:border-[#5cb85c] transition-colors"
                />
              </div>

              {/* Category */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-300 mb-1.5">
                  Service <span className="text-red-400">*</span>
                </label>
                <select
                  name="category"
                  required
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-600 rounded bg-[#0d1117] text-sm text-gray-400 focus:outline-none focus:border-[#5cb85c] transition-colors"
                >
                  <option value="">Select a service...</option>
                  <option value="Social Media Hack">Social Media Hack</option>
                  <option value="Blackhat Hacking">Blackhat Hacking</option>
                  <option value="Penetration Testing">Penetration Testing</option>
                  <option value="Database">Database</option>
                  <option value="Spy">Spy / Surveillance</option>
                  <option value="Mobile Hacking">Mobile Hacking</option>
                  <option value="Email Hacking">Email Hacking</option>
                  <option value="Website Hacking">Website Hacking</option>
                  <option value="Cryptocurrency">Cryptocurrency</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Budget */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-300 mb-1.5">
                  Budget (USD) <span className="text-red-400">*</span>
                </label>
                <select
                  name="budget"
                  required
                  className="w-full px-4 py-2.5 border border-gray-600 rounded bg-[#0d1117] text-sm text-gray-400 focus:outline-none focus:border-[#5cb85c] transition-colors"
                >
                  <option value="">Select your budget...</option>
                  <option value="$100 - $500">$100 - $500</option>
                  <option value="$500 - $1,000">$500 - $1,000</option>
                  <option value="$1,000 - $3,000">$1,000 - $3,000</option>
                  <option value="$3,000 - $5,000">$3,000 - $5,000</option>
                  <option value="$5,000 - $10,000">$5,000 - $10,000</option>
                  <option value="$10,000+">$10,000+</option>
                </select>
              </div>

              {/* Delivery Time */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-300 mb-1.5">Expected Delivery</label>
                <select
                  name="delivery_time"
                  className="w-full px-4 py-2.5 border border-gray-600 rounded bg-[#0d1117] text-sm text-gray-400 focus:outline-none focus:border-[#5cb85c] transition-colors"
                >
                  <option value="">Select timeframe...</option>
                  <option value="Within 24 hours">Within 24 hours</option>
                  <option value="2-3 days">2-3 days</option>
                  <option value="Within 1 week">Within 1 week</option>
                  <option value="Within 2 weeks">Within 2 weeks</option>
                  <option value="Within 1 month">Within 1 month</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </div>

              {/* Description */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-300 mb-1.5">
                  Project Description <span className="text-red-400">*</span>
                </label>
                <p className="text-xs text-gray-400 mb-1.5">
                  Provide as much detail as possible. The more specific you are, the better hackers can assess your project.
                </p>
                <textarea
                  name="description"
                  required
                  maxLength={500}
                  rows={6}
                  placeholder="Describe what you need, including any specific requirements, targets, timelines, and expected outcomes..."
                  className="w-full px-4 py-2.5 border border-gray-600 rounded bg-[#0d1117] text-sm focus:outline-none focus:border-[#5cb85c] transition-colors resize-y"
                />
                <p className="text-xs text-gray-400 mt-1">500 character limit</p>
              </div>

              {/* Payment Method */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-300 mb-1.5">Preferred Payment Method</label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                    <input
                      type="radio"
                      name="payment_method"
                      value="crypto"
                      checked={preferredPayment === "crypto"}
                      onChange={(e) => setPreferredPayment(e.target.value)}
                      className="text-[#5cb85c]"
                    />
                    Cryptocurrency
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                    <input
                      type="radio"
                      name="payment_method"
                      value="escrow"
                      checked={preferredPayment === "escrow"}
                      onChange={(e) => setPreferredPayment(e.target.value)}
                      className="text-[#5cb85c]"
                    />
                    Escrow
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                    <input
                      type="radio"
                      name="payment_method"
                      value="wire"
                      checked={preferredPayment === "wire"}
                      onChange={(e) => setPreferredPayment(e.target.value)}
                      className="text-[#5cb85c]"
                    />
                    Wire Transfer
                  </label>
                </div>
              </div>

              {/* Login prompt for non-logged-in users */}
              {!user && (
                <p className="text-sm font-semibold text-red-400 text-center mt-3">
                  Login or Sign Up to continue posting your project
                </p>
              )}

              {/* Status */}
              {formStatus === "error" && formError && (
                <div className="mb-5 p-3 bg-red-900/20 border border-red-700/30 rounded text-sm text-red-400">
                  <i className="fas fa-exclamation-circle mr-2" />{formError}
                </div>
              )}

              {/* Submit — only shown when logged in */}
              {user && (
                <button
                  type="submit"
                  disabled={popupStatus !== "hidden"}
                  className="w-full bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-semibold py-3 rounded transition-all whitespace-nowrap"
                >
                  {popupStatus === "processing" ? (
                    <span><i className="fas fa-spinner fa-spin mr-2" />Submitting Project...</span>
                  ) : (
                    <span><i className="fas fa-paper-plane mr-2" />Post Project</span>
                  )}
                </button>
              )}
            </form>

            {/* Auth Panel — BELOW the form */}
            {!user && (
              <div ref={authSectionRef} className="mt-6">
                <div className="bg-[#0d1117] rounded-xl border-2 border-[#5cb85c]/20 overflow-hidden">

                {/* Auth Tabs */}
                <div className="flex border-b border-gray-700">
                  {authMode === "forgot" ? (
                    <div className="flex-1 py-3.5 text-center text-sm font-semibold text-[#5cb85c] whitespace-nowrap">
                      RESET PASSWORD
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => { setAuthMode("login"); setAuthStatus("idle"); setAuthError(""); }}
                        className={`flex-1 py-3.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                          authMode === "login"
                            ? "text-[#5cb85c] border-b-2 border-[#5cb85c]"
                            : "text-gray-500 hover:text-gray-300"
                        }`}
                      >
                        LOGIN
                      </button>
                      <button
                        type="button"
                        onClick={() => { setAuthMode("signup"); setAuthStatus("idle"); setAuthError(""); }}
                        className={`flex-1 py-3.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                          authMode === "signup"
                            ? "text-[#5cb85c] border-b-2 border-[#5cb85c]"
                            : "text-gray-500 hover:text-gray-300"
                        }`}
                      >
                        SIGN UP
                      </button>
                    </>
                  )}
                </div>

                {/* Auth Form */}
                <div className="p-6">
                  <form ref={authFormRef} onSubmit={handleAuthSubmit}>
                    {/* Honeypot */}
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="hp-field"
                    />

                    {authMode === "forgot" ? (
                      <>
                        <p className="text-sm text-gray-400 mb-4">
                          Enter your email and we&apos;ll send you a password reset link.
                        </p>
                        <div className="mb-4">
                          <label className="block text-xs font-semibold text-gray-400 mb-1.5">Email Address</label>
                          <input
                            type="email"
                            name="email"
                            required
                            placeholder="you@example.com"
                            className="w-full px-3 py-2.5 border border-gray-600 rounded bg-[#0d1117] text-sm focus:outline-none focus:border-[#5cb85c] transition-colors"
                          />
                        </div>

                        {authStatus === "error" && authError && (
                          <div className="mb-4 p-3 bg-red-900/20 border border-red-700/30 rounded text-sm text-red-400">
                            <i className="fas fa-exclamation-circle mr-2" />{authError}
                          </div>
                        )}
                        {authStatus === "success" && (
                          <div className="mb-4 p-3 bg-green-900/20 border border-green-700/30 rounded text-sm text-green-400">
                            <i className="fas fa-check-circle mr-2" />Reset link sent! Check your email.
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={authStatus === "loading"}
                          className="w-full bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-semibold py-2.5 rounded transition-all whitespace-nowrap"
                        >
                          {authStatus === "loading" ? (
                            <span><i className="fas fa-spinner fa-spin mr-2" />Sending...</span>
                          ) : "SEND RESET LINK"}
                        </button>

                        <p className="text-xs text-gray-500 text-center mt-3">
                          <button
                            type="button"
                            onClick={() => { setAuthMode("login"); setAuthStatus("idle"); setAuthError(""); }}
                            className="text-[#5bc0de] hover:text-[#46b8da] font-semibold"
                          >
                            Back to Login
                          </button>
                        </p>
                      </>
                    ) : (
                      <>
                        {authMode === "signup" && (
                          <div className="mb-4">
                            <label className="block text-xs font-semibold text-gray-400 mb-1.5">Username</label>
                            <input
                              type="text"
                              name="username"
                              required
                              placeholder="Choose a username"
                              className="w-full px-3 py-2.5 border border-gray-600 rounded bg-[#0d1117] text-sm focus:outline-none focus:border-[#5cb85c] transition-colors"
                            />
                          </div>
                        )}

                        <div className="mb-4">
                          <label className="block text-xs font-semibold text-gray-400 mb-1.5">Email Address</label>
                          <input
                            type="email"
                            name="email"
                            required
                            placeholder="you@example.com"
                            className="w-full px-3 py-2.5 border border-gray-600 rounded bg-[#0d1117] text-sm focus:outline-none focus:border-[#5cb85c] transition-colors"
                          />
                        </div>

                        <div className="mb-4">
                          <label className="block text-xs font-semibold text-gray-400 mb-1.5">Password</label>
                          <input
                            type="password"
                            name="password"
                            required
                            placeholder="Enter your password"
                            className="w-full px-3 py-2.5 border border-gray-600 rounded bg-[#0d1117] text-sm focus:outline-none focus:border-[#5cb85c] transition-colors"
                          />
                        </div>

                        {authMode === "signup" && (
                          <div className="mb-4">
                            <label className="block text-xs font-semibold text-gray-400 mb-1.5">Confirm Password</label>
                            <input
                              type="password"
                              name="confirm_password"
                              required
                              placeholder="Confirm your password"
                              className="w-full px-3 py-2.5 border border-gray-600 rounded bg-[#0d1117] text-sm focus:outline-none focus:border-[#5cb85c] transition-colors"
                            />
                          </div>
                        )}

                        {authMode === "login" && (
                          <div className="flex items-center justify-between mb-4">
                            <label className="flex items-center gap-1.5">
                              <input type="checkbox" name="remember_me" className="rounded" />
                              <span className="text-xs text-gray-500">Remember me</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => { setAuthMode("forgot"); setAuthStatus("idle"); setAuthError(""); }}
                              className="text-xs text-[#5bc0de] hover:text-[#46b8da]"
                            >
                              Forgot password?
                            </button>
                          </div>
                        )}

                        {authStatus === "error" && authError && (
                          <div className="mb-4 p-3 bg-red-900/20 border border-red-700/30 rounded text-sm text-red-400">
                            <i className="fas fa-exclamation-circle mr-2" />{authError}
                          </div>
                        )}
                        {authStatus === "success" && authMode === "signup" && (
                          <div className="mb-4 p-3 bg-green-900/20 border border-green-700/30 rounded text-sm text-green-400">
                            <i className="fas fa-check-circle mr-2" />Account created! Check your email to confirm.
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={authStatus === "loading"}
                          className="w-full bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-semibold py-2.5 rounded transition-all whitespace-nowrap"
                        >
                          {authStatus === "loading" ? (
                            <span><i className="fas fa-spinner fa-spin mr-2" />Please wait...</span>
                          ) : authMode === "login" ? "LOGIN" : "CREATE ACCOUNT"}
                        </button>
                      </>
                    )}
                  </form>

                  <p className="text-xs text-gray-500 text-center mt-4">
                    {authMode === "login" ? (
                      <>Don&apos;t have an account?{' '}<button type="button" onClick={() => { setAuthMode("signup"); setAuthStatus("idle"); setAuthError(""); }} className="text-[#5bc0de] hover:text-[#46b8da] font-semibold">Sign Up</button></>
                    ) : (
                      <>Already have an account?{' '}<button type="button" onClick={() => { setAuthMode("login"); setAuthStatus("idle"); setAuthError(""); }} className="text-[#5bc0de] hover:text-[#46b8da] font-semibold">Login</button></>
                    )}
                  </p>
                </div>
              </div>
                <div className="p-4 bg-red-900/20 border border-red-700/30 rounded mt-4">
                  <p className="text-sm font-semibold text-red-400 leading-relaxed">
                    <i className="fas fa-shield-alt mr-1" />
                    Important: By posting a project, you agree to Hackers Lodge Terms of Service.
                    All projects are handled with strict confidentiality. We recommend using our escrow service
                    for secure payments. Never share sensitive personal information upfront.
                  </p>
                </div>
              </div>
            )}

            {/* Back Link */}
            <div className="text-center mt-6">
              <Link
                to="/post-project"
                className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
              >
                <i className="fas fa-arrow-left mr-1" /> Back to Service Selection
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Processing / Success Popup Overlay */}
      {popupStatus !== "hidden" && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0d1117]/95 backdrop-blur-sm transition-all duration-300">
          {/* Processing Phase */}
          {popupStatus === "processing" && (
            <div className="text-center px-6 max-w-sm w-full animate-[fadeIn_0.4s_ease-out]">
              {/* Spinner ring */}
              <div className="relative w-20 h-20 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full border-2 border-gray-700" />
                <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#5cb85c] animate-spin" />
                <div className="absolute inset-2 rounded-full border-2 border-transparent border-t-[#4cae4c] animate-spin" style={{ animationDuration: "2s" }} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <i className="fas fa-paper-plane text-[#5cb85c] text-lg" />
                </div>
              </div>

              <h2 className="text-lg font-bold text-gray-200 mb-2">Submitting to Queue</h2>
              <p className="text-sm text-gray-400 mb-4">
                We&apos;re sending <strong className="text-gray-300">&quot;{submittedTitle || "your project"}&quot;</strong> for review
              </p>

              {/* Progress dots */}
              <div className="flex items-center justify-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-[#5cb85c] animate-pulse" />
                <div className="w-2 h-2 rounded-full bg-[#5cb85c] animate-pulse" style={{ animationDelay: "0.2s" }} />
                <div className="w-2 h-2 rounded-full bg-[#5cb85c] animate-pulse" style={{ animationDelay: "0.4s" }} />
              </div>
              <p className="text-xs text-gray-500">This won&apos;t take long</p>
            </div>
          )}

          {/* Success Phase */}
          {popupStatus === "success" && (
            <div className="text-center px-6 max-w-sm w-full animate-[scaleIn_0.5s_ease-out]">
              {/* Checkmark circle */}
              <div className="relative w-20 h-20 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full bg-[#5cb85c]/10 border-2 border-[#5cb85c]/30 animate-[popIn_0.6s_ease-out]" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-[#5cb85c] flex items-center justify-center animate-[checkBounce_0.5s_ease-out_0.3s_both]">
                    <i className="fas fa-check text-white text-lg" />
                  </div>
                </div>
              </div>

              <h2 className="text-xl font-bold text-gray-200 mb-2">Project Queued!</h2>
              <p className="text-sm text-gray-400 mb-2 leading-relaxed">
                <strong className="text-gray-300">&quot;{submittedTitle || "Your project"}&quot;</strong> has been submitted for review.
              </p>
              <p className="text-xs text-gray-500 mb-6 leading-relaxed">
                Hackers can view your project once approved. You&apos;ll get email notifications when it goes live.
              </p>

              {/* CTA */}
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-sm font-semibold py-3 rounded-lg transition-all whitespace-nowrap mb-3"
              >
                <i className="fas fa-chart-bar mr-2" />
                View My Projects
              </button>

              <button
                onClick={() => {
                  setPopupStatus("hidden");
                  navigate("/dashboard");
                }}
                className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          )}
        </div>
      )}

      {/* Keyframe animations injected via style tag */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.92); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes popIn {
          0%   { transform: scale(0.6); opacity: 0; }
          60%  { transform: scale(1.08); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes checkBounce {
          0%   { transform: scale(0); }
          60%  { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
}